import type {
  CargoEventCreateInput,
  CargoEventDto,
  CarrierCreateInput,
  CarrierDto,
  CarrierPatchInput,
  ContractPartyCreateInput,
  ContractPartyDto,
  GeoTrackBatchInput,
  GeoTrackPointCreateInput,
  GeoTrackPointDto,
  ShipmentDetail,
  ShipmentLegDto,
  ShipmentLegInput,
  ShipmentListItem,
  ShipmentUnitsPatchInput,
} from '@expeditor/shared';
import { apiClient } from './client';

export const shipmentsApi = {
  list(): Promise<ShipmentListItem[]> {
    return apiClient.get<ShipmentListItem[]>('/shipments').then((r) => r.data);
  },
  getOne(id: string): Promise<ShipmentDetail> {
    return apiClient.get<ShipmentDetail>(`/shipments/${id}`).then((r) => r.data);
  },
  close(id: string): Promise<ShipmentDetail> {
    return apiClient.post<ShipmentDetail>(`/shipments/${id}/close`).then((r) => r.data);
  },
  /** Ручное создание перевозки-проекта от поручения. */
  createProject(orderId: string): Promise<{ id: string }> {
    return apiClient
      .post<{ id: string }>(`/orders/${orderId}/shipment-project`)
      .then((r) => r.data);
  },
  // --- Плечи перевозки (фаза 2) ---
  listLegs(shipmentId: string): Promise<ShipmentLegDto[]> {
    return apiClient.get<ShipmentLegDto[]>(`/shipments/${shipmentId}/legs`).then((r) => r.data);
  },
  createLeg(shipmentId: string, dto: ShipmentLegInput): Promise<ShipmentLegDto> {
    return apiClient
      .post<ShipmentLegDto>(`/shipments/${shipmentId}/legs`, dto)
      .then((r) => r.data);
  },
  updateLeg(shipmentId: string, legId: string, dto: ShipmentLegInput): Promise<ShipmentLegDto> {
    return apiClient
      .patch<ShipmentLegDto>(`/shipments/${shipmentId}/legs/${legId}`, dto)
      .then((r) => r.data);
  },
  removeLeg(shipmentId: string, legId: string): Promise<{ deleted: true }> {
    return apiClient
      .delete<{ deleted: true }>(`/shipments/${shipmentId}/legs/${legId}`)
      .then((r) => r.data);
  },
  updateUnits(id: string, dto: ShipmentUnitsPatchInput): Promise<ShipmentDetail> {
    return apiClient.patch<ShipmentDetail>(`/shipments/${id}/units`, dto).then((r) => r.data);
  },
  setPossession(id: string, expeditorTakesPossession: boolean): Promise<ShipmentDetail> {
    return apiClient
      .patch<ShipmentDetail>(`/shipments/${id}/possession`, { expeditorTakesPossession })
      .then((r) => r.data);
  },
  addCargoEvent(shipmentId: string, dto: CargoEventCreateInput): Promise<CargoEventDto> {
    return apiClient
      .post<CargoEventDto>(`/shipments/${shipmentId}/events`, dto)
      .then((r) => r.data);
  },
  addGeoPoint(shipmentId: string, dto: GeoTrackPointCreateInput): Promise<GeoTrackPointDto> {
    return apiClient
      .post<GeoTrackPointDto>(`/shipments/${shipmentId}/geotrack`, dto)
      .then((r) => r.data);
  },
  addGeoBatch(shipmentId: string, dto: GeoTrackBatchInput): Promise<{ count: number }> {
    return apiClient
      .post<{ count: number }>(`/shipments/${shipmentId}/geotrack/batch`, dto)
      .then((r) => r.data);
  },
  listPartiesForContract(contractId: string): Promise<ContractPartyDto[]> {
    return apiClient
      .get<ContractPartyDto[]>(`/contracts/${contractId}/parties`)
      .then((r) => r.data);
  },
  addParty(contractId: string, dto: ContractPartyCreateInput): Promise<ContractPartyDto> {
    return apiClient
      .post<ContractPartyDto>(`/contracts/${contractId}/parties`, dto)
      .then((r) => r.data);
  },
  removeParty(contractId: string, partyId: string): Promise<{ deleted: true }> {
    return apiClient
      .delete<{ deleted: true }>(`/contracts/${contractId}/parties/${partyId}`)
      .then((r) => r.data);
  },
};

export const carriersApi = {
  list(includeArchived = false): Promise<CarrierDto[]> {
    return apiClient
      .get<CarrierDto[]>(`/carriers${includeArchived ? '?includeArchived=true' : ''}`)
      .then((r) => r.data);
  },
  create(dto: CarrierCreateInput): Promise<CarrierDto> {
    return apiClient.post<CarrierDto>('/carriers', dto).then((r) => r.data);
  },
  update(id: string, patch: CarrierPatchInput): Promise<CarrierDto> {
    return apiClient.patch<CarrierDto>(`/carriers/${id}`, patch).then((r) => r.data);
  },
  archive(id: string): Promise<CarrierDto> {
    return apiClient.delete<CarrierDto>(`/carriers/${id}`).then((r) => r.data);
  },
  restore(id: string): Promise<CarrierDto> {
    return apiClient.post<CarrierDto>(`/carriers/${id}/restore`).then((r) => r.data);
  },
};
